import React, { useEffect, useState } from "react";
import { collection, getDocs, doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../config/firebase";
import PageTemplate from "../templates/PageTemplate";
import moment from "moment";
import { useNavigate } from "react-router-dom";

const STATUS_LABELS = {
  pending: "En attente",
  confirmed: "Confirmé",
  inProgress: "En cours",
  waitPayment: "En attente paiement",
  paymentConfirmed: "Paiement confirmé",
  refused: "Refusé",
  cancelled: "Annulé",
};

const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
  confirmed: "bg-blue-100 text-blue-800 border-blue-200",
  inProgress: "bg-indigo-100 text-indigo-800 border-indigo-200",
  waitPayment: "bg-orange-100 text-orange-800 border-orange-200",
  paymentConfirmed: "bg-green-100 text-green-800 border-green-200",
  refused: "bg-red-100 text-red-800 border-red-200",
  cancelled: "bg-gray-100 text-gray-800 border-gray-200",
};

// fonction pour tronquer texte
function truncate(text, max = 50) {
  if (!text) return "-";
  return text.length > max ? text.slice(0, max) + "…" : text;
}

export default function AppointmentsList() {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [updatingId, setUpdatingId] = useState(null);
  const navigate = useNavigate();

  const showNotif = (msg) => {
    const div = document.createElement("div");
    div.className =
      "fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 text-sm";
    div.textContent = msg;
    document.body.appendChild(div);
    setTimeout(() => div.remove(), 3000);
  };

  const updateStatus = async (id, newStatus) => {
    setUpdatingId(id);
    try {
      await updateDoc(doc(db, "appointments", id), { status: newStatus });
      setAppointments((prev) =>
        prev.map((a) => (a.id === id ? { ...a, status: newStatus } : a))
      );
      showNotif("Statut mis à jour !");
    } catch {
      alert("Erreur lors de la mise à jour");
    } finally {
      setUpdatingId(null);
    }
  };

  useEffect(() => {
    async function fetchAppointments() {
      setLoading(true);
      const snap = await getDocs(collection(db, "appointments"));
      const data = [];

      for (const docSnap of snap.docs) {
        const appoint = docSnap.data();

        let clientEmail = "";
        let couturierEmail = "";
        let offerMessage = "";

        if (appoint.clientId && typeof appoint.clientId === "string") {
          const clientSnap = await getDoc(doc(db, "users", appoint.clientId));
          if (clientSnap.exists()) clientEmail = clientSnap.data().email || "";
        }
        if (appoint.couturierId && typeof appoint.couturierId === "string") {
          const couturierSnap = await getDoc(
            doc(db, "users", appoint.couturierId)
          );
          if (couturierSnap.exists())
            couturierEmail = couturierSnap.data().email || "";
        }
        if (appoint.offerId && typeof appoint.offerId === "string") {
          const offerSnap = await getDoc(doc(db, "offers", appoint.offerId));
          if (offerSnap.exists()) {
            offerMessage = offerSnap.data().message || "";
          }
        }

        data.push({
          id: docSnap.id,
          clientEmail,
          couturierEmail,
          date: appoint.date,
          status: appoint.status,
          paidAt: appoint.paidAt,
          offerMessage,
          createdAt: appoint.createdAt,
        });
      }

      // Trier par date de création (plus récent en premier)
      data.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
        return dateB - dateA;
      });

      setAppointments(data);
      setLoading(false);
    }
    fetchAppointments();
  }, []);

  const filteredAppointments =
    filterStatus === "all"
      ? appointments
      : appointments.filter((a) => a.status === filterStatus);

  const statusCounts = {
    all: appointments.length,
    pending: appointments.filter((a) => a.status === "pending").length,
    confirmed: appointments.filter((a) => a.status === "confirmed").length,
    waitPayment: appointments.filter((a) => a.status === "waitPayment").length,
    paymentConfirmed: appointments.filter((a) => a.status === "paymentConfirmed")
      .length,
  };

  return (
    <PageTemplate
      loading={loading}
      content={
        <div className="space-y-4">
          {/* Filter tabs */}
          <div className="flex flex-wrap items-center gap-1 bg-white rounded-lg border border-gray-200 p-1">
            {[
              { key: "all", label: "Tous" },
              { key: "pending", label: "En attente" },
              { key: "confirmed", label: "Confirmés" },
              { key: "waitPayment", label: "Att. paiement" },
              { key: "paymentConfirmed", label: "Payés" },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setFilterStatus(key)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  filterStatus === key
                    ? "bg-gray-900 text-white"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                {label} ({statusCounts[key]})
              </button>
            ))}
          </div>

          {/* Table */}
          <div className="bg-white rounded-lg border border-gray-200">
            {filteredAppointments.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-sm text-gray-500">Aucun rendez-vous trouvé</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Couturier</th>
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Date RDV</th>
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Offre</th>
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Créé le</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredAppointments.map((a) => (
                      <tr key={a.id} onClick={() => navigate(`/rdv/${a.id}`)} className="hover:bg-gray-50 cursor-pointer">
                        <td className="px-3 py-2.5 text-gray-900">{a.clientEmail || <span className="text-xs italic text-gray-400">Inconnu</span>}</td>
                        <td className="px-3 py-2.5 text-gray-900">{a.couturierEmail || <span className="text-xs italic text-gray-400">Inconnu</span>}</td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-sm">
                          {a.date ? moment(a.date).format("DD/MM/YYYY HH:mm") : "-"}
                        </td>
                        <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                          <select
                            disabled={updatingId === a.id}
                            value={a.status}
                            onChange={(e) => updateStatus(a.id, e.target.value)}
                            className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#801111] focus:border-[#801111] disabled:opacity-50"
                          >
                            {Object.entries(STATUS_LABELS).map(([key, label]) => (
                              <option key={key} value={key}>{label}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2.5 max-w-xs" title={a.offerMessage}>
                          <span className="text-xs text-gray-600">{truncate(a.offerMessage, 40)}</span>
                        </td>
                        <td className="px-3 py-2.5 text-gray-500 whitespace-nowrap text-xs">
                          {a.createdAt && a.createdAt.toDate
                            ? moment(a.createdAt.toDate()).format("DD/MM/YYYY HH:mm")
                            : a.createdAt ? moment(a.createdAt).format("DD/MM/YYYY HH:mm") : "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      }
    />
  );
}
